<template>
  <section class="payment-result-page" role="main" aria-live="polite">
    <div class="payment-result-glow" />
    <article class="payment-result-card">
      <header>
        <span :class="['payment-result-mark', state]"><i :class="stateIcon" /></span>
        <small>STARLIVE SECURE PAYMENT</small>
        <h1>{{ $t(`paymentResult.${state}Title`) }}</h1>
        <p>{{ $t(`paymentResult.${state}Description`) }}</p>
        <p v-if="browserReturnHint" class="payment-result-browser-hint"><i class="el-icon-mobile-phone" />{{ $t("paymentResult.browserReturnHint") }}</p>
      </header>

      <dl v-if="order">
        <div><dt>{{ $t("paymentResult.order") }}</dt><dd>{{ order.trade_no }}</dd></div>
        <div><dt>{{ $t("paymentResult.total") }}</dt><dd>{{ $currency(order.paid_amount, order.currency) }}</dd></div>
        <div><dt>{{ $t("paymentResult.coins") }}</dt><dd>{{ $n(order.coin_amount) }}</dd></div>
      </dl>

      <div v-if="state === 'checking'" class="payment-result-progress"><span /></div>
      <button v-if="state === 'failed' || state === 'canceled'" type="button" class="payment-result-primary" :disabled="checking" @click="checkNow">
        <i v-if="checking" class="el-icon-loading" />{{ $t("paymentResult.checkAgain") }}
      </button>
      <button v-else type="button" class="payment-result-primary" @click="finish">{{ $t(state === "success" ? "paymentResult.viewWallet" : "paymentResult.backHome") }}</button>
      <button v-if="state === 'failed' || state === 'canceled'" type="button" class="payment-result-secondary" @click="finish">{{ $t("paymentResult.backHome") }}</button>
      <small class="payment-result-trust"><i class="el-icon-lock" />{{ $t("paymentResult.serverVerified") }}</small>
    </article>
  </section>
</template>

<script>
import { capturePayPalOrder, getPaymentStatus } from "../api/center"
import { claimPendingPaymentResume, clearPendingPayment, pendingPayment } from "../utils/paymentCheckout"
import { isIOS, isStandalone } from "../utils/pwa"

export default {
  name: "PaymentResult",
  data() {
    return {
      state: "checking",
      order: null,
      checking: false,
      timer: null,
      attempts: 0,
      reference: null,
      captureAttempted: false,
      settledNotified: false
    }
  },
  computed: {
    stateIcon() {
      return { checking: "el-icon-loading", success: "el-icon-check", failed: "el-icon-close", canceled: "el-icon-back" }[this.state]
    },
    browserReturnHint() {
      return isIOS() && !isStandalone()
    }
  },
  mounted() {
    this.reference = claimPendingPaymentResume() || pendingPayment()
    document.addEventListener("visibilitychange", this.handleVisibility)
    if (!this.reference) {
      this.state = "failed"
      return
    }
    const params = new URLSearchParams(window.location.search)
    if (params.get("canceled") === "1") this.state = "canceled"
    this.checkNow()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
    document.removeEventListener("visibilitychange", this.handleVisibility)
  },
  methods: {
    async checkNow() {
      if (this.checking || !this.reference || document.hidden) return
      clearTimeout(this.timer)
      this.checking = true
      try {
        const params = new URLSearchParams(window.location.search)
        if (this.reference.channel === "paypal" && params.get("token") && !this.captureAttempted && params.get("canceled") !== "1") {
          this.captureAttempted = true
          const captured = await capturePayPalOrder({ order_id: this.reference.orderId })
          this.order = captured.data && captured.data.order
        }
        const response = await getPaymentStatus({ order_id: this.reference.orderId })
        this.order = response.data && response.data.order
        const status = Number((this.order || {}).status)
        if (status === 2) {
          this.state = "success"
          clearPendingPayment()
          if (!this.settledNotified) {
            this.settledNotified = true
            this.$emit("settled", this.order)
          }
          return
        }
        if ([3, 4, 5].includes(status)) {
          this.state = status === 4 ? "canceled" : "failed"
          clearPendingPayment()
          return
        }
        this.state = params.get("canceled") === "1" ? "canceled" : "checking"
        this.scheduleNext()
      } catch (_error) {
        this.state = this.state === "canceled" ? "canceled" : "checking"
        this.scheduleNext()
      } finally {
        this.checking = false
      }
    },
    scheduleNext() {
      if (this.attempts >= 12 || document.hidden) return
      const delays = [1000, 1600, 2500, 4000, 6000, 8000]
      const delay = delays[Math.min(this.attempts, delays.length - 1)]
      this.attempts += 1
      this.timer = setTimeout(this.checkNow, delay)
    },
    handleVisibility() {
      if (!document.hidden && ["checking", "canceled"].includes(this.state)) this.checkNow()
    },
    finish() {
      clearPendingPayment()
      window.history.replaceState({}, "", "/")
      this.$emit("close")
    }
  }
}
</script>

<style scoped>
.payment-result-page{position:fixed;inset:0;z-index:1200;display:grid;place-items:center;padding:24px;background:radial-gradient(circle at 50% 8%,rgba(255,96,74,.24),transparent 38%),#0d0e10;color:#f8f4ed;overflow:hidden}
.payment-result-glow{position:absolute;width:340px;height:340px;border:1px solid rgba(255,255,255,.07);border-radius:50%;box-shadow:0 0 0 38px rgba(255,255,255,.018),0 0 0 82px rgba(255,255,255,.012)}
.payment-result-card{position:relative;width:min(100%,390px);padding:30px 24px 22px;border:1px solid rgba(255,255,255,.1);border-radius:28px;background:linear-gradient(155deg,rgba(35,36,41,.98),rgba(20,21,25,.98));box-shadow:0 28px 80px rgba(0,0,0,.48);text-align:center}
.payment-result-card header{display:grid;justify-items:center}.payment-result-mark{width:68px;height:68px;display:grid;place-items:center;margin-bottom:18px;border-radius:22px;background:rgba(255,178,93,.12);color:#ffb15f;font-size:29px}.payment-result-mark.success{background:rgba(63,207,142,.13);color:#55dda0}.payment-result-mark.failed,.payment-result-mark.canceled{background:rgba(255,76,92,.12);color:#ff6c7b}.payment-result-card header>small{color:#ffad69;font-size:9px;font-weight:900;letter-spacing:.18em}.payment-result-card h1{margin:9px 0 8px;font-size:27px}.payment-result-card p{min-height:42px;margin:0;color:#a8a9b0;font-size:12px;line-height:1.7}
.payment-result-card .payment-result-browser-hint{min-height:0;display:flex;align-items:flex-start;gap:8px;margin-top:12px;padding:11px 12px;border:1px solid rgba(255,173,105,.2);border-radius:13px;background:rgba(255,173,105,.08);color:#ffd0a1;text-align:start}.payment-result-browser-hint i{flex:none;margin-top:2px;color:#ffad69;font-size:15px}
.payment-result-card dl{margin:22px 0;padding:5px 15px;border:1px solid rgba(255,255,255,.07);border-radius:17px;background:rgba(255,255,255,.025)}.payment-result-card dl div{display:flex;justify-content:space-between;gap:18px;padding:11px 0;border-bottom:1px solid rgba(255,255,255,.06)}.payment-result-card dl div:last-child{border:0}.payment-result-card dt{color:#858891;font-size:10px}.payment-result-card dd{max-width:220px;margin:0;overflow:hidden;color:#f6d5a8;font-size:11px;font-weight:800;text-overflow:ellipsis;white-space:nowrap}
.payment-result-progress{height:3px;margin:20px 0;border-radius:4px;background:#33343a;overflow:hidden}.payment-result-progress span{display:block;width:40%;height:100%;border-radius:inherit;background:linear-gradient(90deg,#ff3858,#ffae63);animation:payment-progress 1.2s ease-in-out infinite}.payment-result-primary,.payment-result-secondary{width:100%;min-height:48px;border-radius:14px;font:inherit;font-weight:900}.payment-result-primary{border:0;color:#fff;background:linear-gradient(105deg,#ff3154,#ff704d);box-shadow:0 12px 30px rgba(255,49,84,.22)}.payment-result-secondary{margin-top:8px;border:1px solid #37383f;color:#b9bac0;background:transparent}.payment-result-trust{display:flex;align-items:center;justify-content:center;gap:5px;margin-top:15px;color:#747780;font-size:9px}
@keyframes payment-progress{0%{transform:translateX(-110%)}100%{transform:translateX(250%)}}
@media (max-height:620px){.payment-result-card{padding-top:20px}.payment-result-mark{width:54px;height:54px;margin-bottom:10px}.payment-result-card dl{margin:13px 0}}
</style>
